import { DIRS, hexDist, hexLen, hexToWorld, type Hex } from '../hex.ts';
import { idiv } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng, type RngState } from '../math/rng.ts';
import { MIRROR_TURN } from './litter-layout.ts';

/**
 * The cleric's last road, chapter 4 (docs/sendas/clerigo.md, «La Ermita»): the nave of the Hermitage of the Crag, the
 * same from the seed in the sim and the drawing, integers only. Up is −y (the top of the screen).
 *
 * The nave is a long hall cut in the rock, north to south: the altar at its north end (where the one who goes is laid),
 * its door at the south end (where those who stay keep watch), the atrium beyond the door; a broken beam over its middle,
 * under the hole in the roof, the lantern hung from it. Six great mirrors on its columns and two dividers of glass.
 *
 * - `naveTrace`: the beam from the lantern along one of the six directions, cell by cell. A mirror always turns it (a sixth
 *   to one side, to the other, or back: never straight on); a divider lets it on straight and sends another at a sixth to
 *   one side or the other (two positions). The lantern is weak: a beam that has gone through two dividers lights nothing.
 *   A wall, the lantern or a mirror under a limpet stops it; the altar and the door take it.
 * - `naveLayout`: the puzzle (§4, «La nave de los espejos»), built by the seed. The lantern's beam meets a first mirror:
 *   turned one way it goes on by one more mirror to the altar (the obvious way, two mirrors and no divider: the door stays
 *   dark); turned the other it goes to a divider, whose two beams each reach the altar and the door (by a mirror, or
 *   straight). The other divider stands right by that way: a mirror turned wrong sends the beam through it, and it splits
 *   too much. The rest are mirrors that lead nowhere. Every nave is tried against all 2 916 ways of setting its six mirrors
 *   and two dividers, counting only the ones the beam touches: only naves with one way that lights both are kept.
 *   Aud's mirror is the one by the altar on that way: it starts out of its place (the hero sets it there, §3 act 3).
 * - The memory (act 2) is played in the same nave sixty-two years before: its six lamps from the door to the altar
 *   (`lamps`), the last one the altar's.
 */

export const NAVE_RADIUS = 8;
/** A divider's position (0–1): its second beam a sixth to one side or the other. */
export const SPLIT_TURN = [1, 5] as const;

const sortHex = (a: Hex, b: Hex): number => a.r - b.r || a.q - b.q;
const same = (a: Hex, b: Hex): boolean => a.q === b.q && a.r === b.r;
const step = (h: Hex, d: number, k = 1): Hex => {
  const D = DIRS[((d % 6) + 6) % 6] as Hex;
  return { q: h.q + D.q * k, r: h.r + D.r * k };
};

/** The nave's floor: eight rows each side of the middle, four and a half hexes each side of its axis. */
export const inNave = (q: number, r: number): boolean => r >= -7 && r <= 7 && Math.abs(2 * q + r) <= 9;

export interface NaveBoard {
  lantern: { q: number; r: number; dir: number };
  altar: Hex;
  door: Hex;
  mirrors: Hex[];
  splits: Hex[];
}

export interface NaveBeam {
  /** Its straight pieces, as cells: from, to (four numbers each: q, r, q, r). */
  segs: number[];
  /** Each piece's dividers gone through (0 bright, 1 still bright, 2 or more: faint, it lights nothing). */
  weak: number[];
  altar: boolean;
  door: boolean;
  /** The mirrors (bits 0–5) and dividers (bits 6–7) the beam touched. */
  touched: number;
}

/** The beam with the mirrors as `turns`, the dividers as `splits`; `out`: mirrors not there (bits); `covered`: mirrors under a limpet (bits). */
export const naveTrace = (B: NaveBoard, turns: readonly number[], splits: readonly number[], out = 0, covered = 0): NaveBeam => {
  const res: NaveBeam = { segs: [], weak: [], altar: false, door: false, touched: 0 };
  const stack: number[] = [B.lantern.q, B.lantern.r, B.lantern.dir, 0];
  const seen = new Set<number>();
  let budget = 400;
  while (stack.length && budget > 0) {
    const k = stack.pop() as number;
    const d0 = stack.pop() as number;
    const r0 = stack.pop() as number;
    const q0 = stack.pop() as number;
    let q = q0;
    let r = r0;
    let d = d0;
    let sq = q0;
    let sr = r0;
    const end = (): void => {
      res.segs.push(sq, sr, q, r);
      res.weak.push(k);
    };
    for (;;) {
      if (--budget <= 0) {
        end();
        break;
      }
      const D = DIRS[d] as Hex;
      q += D.q;
      r += D.r;
      if (!inNave(q, r) || (q === B.lantern.q && r === B.lantern.r)) {
        end();
        break;
      }
      if (q === B.altar.q && r === B.altar.r) {
        end();
        if (k <= 1) res.altar = true;
        break;
      }
      if (q === B.door.q && r === B.door.r) {
        end();
        if (k <= 1) res.door = true;
        break;
      }
      const m = B.mirrors.findIndex((o) => o.q === q && o.r === r);
      if (m >= 0 && !((out >> m) & 1)) {
        end();
        if ((covered >> m) & 1) {
          res.touched |= 1 << m;
          break;
        }
        res.touched |= 1 << m;
        const key = ((q + 16) * 40 + (r + 16)) * 32 + d * 4 + (k > 3 ? 3 : k);
        if (seen.has(key)) break;
        seen.add(key);
        d = (d + (MIRROR_TURN[(turns[m] ?? 0) % 3] as number)) % 6;
        sq = q;
        sr = r;
        continue;
      }
      const sp = B.splits.findIndex((o) => o.q === q && o.r === r);
      if (sp >= 0) {
        end();
        res.touched |= 1 << (6 + sp);
        const key = ((q + 16) * 40 + (r + 16)) * 32 + d * 4 + (k > 3 ? 3 : k);
        if (seen.has(key)) break;
        seen.add(key);
        // Straight on, and a sixth aside: both a divider more on.
        stack.push(q, r, d, k + 1);
        stack.push(q, r, (d + (SPLIT_TURN[(splits[sp] ?? 0) % 2] as number)) % 6, k + 1);
        break;
      }
    }
  }
  return res;
};

/** The key of a way of setting the nave by what the beam touches: each touched mirror's and divider's position. */
const keyOf = (touched: number, turns: readonly number[], splits: readonly number[]): number => {
  let k = touched;
  for (let i = 0; i < 6; i++) if ((touched >> i) & 1) k = k * 3 + ((turns[i] ?? 0) % 3);
  for (let i = 0; i < 2; i++) if ((touched >> (6 + i)) & 1) k = k * 2 + ((splits[i] ?? 0) % 2);
  return k;
};

/** Every way that lights both, told apart only by what the beam touches (the first such setting of each). */
export const naveSolve = (B: NaveBoard): { turns: number[]; splits: number[]; touched: number }[] => {
  const found = new Map<number, { turns: number[]; splits: number[]; touched: number }>();
  const t = [0, 0, 0, 0, 0, 0];
  const sp = [0, 0];
  for (let c = 0; c < 729 * 4; c++) {
    let x = c;
    for (let i = 0; i < 6; i++) {
      t[i] = x % 3;
      x = idiv(x, 3);
    }
    sp[0] = x % 2;
    sp[1] = idiv(x, 2) % 2;
    const b = naveTrace(B, t, sp);
    if (!b.altar || !b.door) continue;
    const k = keyOf(b.touched, t, sp);
    if (!found.has(k)) found.set(k, { turns: [...t], splits: [...sp], touched: b.touched });
  }
  // The same way entered by a bounce (the obvious mirror set to «back» sends the beam back to the first, and the first
  // on to the divider: geometry makes that always so) is the same way: told apart by the light from the divider on.
  const seen = new Map<string, { turns: number[]; splits: number[]; touched: number }>();
  for (const v of found.values()) {
    const b = naveTrace(B, v.turns, v.splits);
    const parts: string[] = [];
    for (let i = 0; i < b.weak.length; i++) if ((b.weak[i] as number) >= 1) parts.push(b.segs.slice(i * 4, i * 4 + 4).join(','));
    const sig = parts.sort().join('|');
    const old = seen.get(sig);
    // (The plainer one kept: fewer touched.)
    if (!old || popcount(v.touched) < popcount(old.touched)) seen.set(sig, v);
  }
  return [...seen.values()];
};

export interface NaveLayout extends NaveBoard {
  radius: number;
  /** What can't be walked: the rock round the nave, the columns the mirrors and dividers stand on, the altar. */
  rocks: Hex[];
  /** The answer: each mirror's and divider's position on the way that lights both (the untouched ones as they start). */
  answer: { turns: number[]; splits: number[]; touched: number };
  /** The obvious way to the altar: its turns, the mirrors it uses (bits). */
  obvious: { turns: number[]; touched: number };
  /** How the mirrors and dividers start. */
  start0: number[];
  split0: number[];
  /** Aud's mirror (by number): out of its place at the start, in the loose stone behind the altar. */
  aud: number;
  stone: { x: number; y: number };
  /** Where the hero stands to turn each mirror and divider (toward the nave's axis). */
  mirrorStand: { x: number; y: number }[];
  splitStand: { x: number; y: number }[];
  /** Tessa's place on the altar; Piet's beside her; the hero's start, by the door. */
  rest: { x: number; y: number };
  piet: { x: number; y: number };
  start: { x: number; y: number };
  /** The ink on the walls (where the limpets come out of) and the cells' doors (where the dead come in). */
  ink: { x: number; y: number }[];
  cells: { x: number; y: number }[];
  /** The memory's six lamps, from the door to the altar (the last one the altar's). */
  lamps: { x: number; y: number }[];
  /** The atrium's door, past the nave's door: where the Doliente comes in, in the memory. */
  atrium: { x: number; y: number };
}

/** Cells along a line from `h` in `d` (not `h` itself) while on the floor. */
const ray = (h: Hex, d: number): Hex[] => {
  const out: Hex[] = [];
  let c = h;
  for (let i = 0; i < 20; i++) {
    c = step(c, d);
    if (!inNave(c.q, c.r)) break;
    out.push(c);
  }
  return out;
};

const pick = <T>(rng: RngState, xs: readonly T[]): T | undefined => (xs.length ? xs[rngInt(rng, xs.length)] : undefined);

/** A turn of a mirror that sends a beam coming in along `d` straight to `to` from `at` (its position), or −1. */
const aimAt = (at: Hex, d: number, to: Hex): number[] => {
  const out: number[] = [];
  for (let t = 0; t < 2; t++) {
    const e = (d + (MIRROR_TURN[t] as number)) % 6;
    if (ray(at, e).some((c) => same(c, to))) out.push(t);
  }
  return out;
};

/** The cells a piece of the beam crosses, from `a` along `d` to `b` (b included). */
const between = (a: Hex, d: number, b: Hex): Hex[] => {
  const out: Hex[] = [];
  let c = a;
  for (let i = 0; i < 20; i++) {
    c = step(c, d);
    out.push(c);
    if (same(c, b)) return out;
  }
  return out;
};

interface Draft {
  m: Hex[];
  sp: Hex[];
  turns: number[];
  splits: number[];
  path: Hex[];
  obvious: { m: Hex; t: number }[];
  m1t: number;
}

/** One try at the nave's puzzle (null: this draw doesn't make one). */
const draft = (rng: RngState, L: { q: number; r: number; dir: number }, A: Hex, D: Hex): Draft | null => {
  const used: Hex[] = [{ q: L.q, r: L.r }, A, D];
  const path: Hex[] = [];
  const free = (h: Hex): boolean => inNave(h.q, h.r) && !used.some((u) => same(u, h));
  // The first mirror, two or three cells out of the lantern.
  const m1 = ray(L, L.dir)[1 + rngInt(rng, 2)];
  if (!m1 || !free(m1)) return null;
  path.push(...between(L, L.dir, m1));
  used.push(m1);
  const order = rngInt(rng, 2) === 0 ? [0, 1] : [1, 0];
  const tO = order[0] as number;
  const tS = order[1] as number;
  // The obvious way: one more mirror on the first's other turn, to the altar.
  const dO = (L.dir + (MIRROR_TURN[tO] as number)) % 6;
  const oc: { c: Hex; t: number }[] = [];
  ray(m1, dO).forEach((c, i) => {
    if (i < 1 || !free(c) || hexDist(c, A) < 2) return;
    for (const t of aimAt(c, dO, A)) oc.push({ c, t });
  });
  const o = pick(rng, oc);
  if (!o) return null;
  used.push(o.c);
  const oPath = [...between(m1, dO, o.c), ...between(o.c, (dO + (MIRROR_TURN[o.t] as number)) % 6, A)];
  // The way that lights both: the first mirror's other turn, to the divider (by a mirror or straight).
  const dS = (L.dir + (MIRROR_TURN[tS] as number)) % 6;
  type Leg = { at: Hex; d: number; m: { c: Hex; t: number }[]; cells: Hex[] };
  const legsTo = (from: Hex, d: number, to: Hex): Leg[] => {
    const out: Leg[] = [];
    const r0 = ray(from, d);
    // Straight there.
    const k = r0.findIndex((c) => same(c, to));
    if (k >= 0 && r0.slice(0, k).every((c) => free(c))) out.push({ at: from, d, m: [], cells: r0.slice(0, k + 1) });
    // By one mirror, or by two.
    for (let i = 1; i < r0.length; i++) {
      const c = r0[i] as Hex;
      if (!free(c) || r0.slice(0, i).some((x) => !free(x))) break;
      for (const t of aimAt(c, d, to)) {
        const e = (d + (MIRROR_TURN[t] as number)) % 6;
        const cs = between(c, e, to);
        if (cs.slice(0, -1).every((x) => free(x))) out.push({ at: from, d, m: [{ c, t }], cells: [...r0.slice(0, i + 1), ...cs] });
      }
      for (let t = 0; t < 2; t++) {
        const e = (d + (MIRROR_TURN[t] as number)) % 6;
        const r2 = ray(c, e);
        for (let j = 1; j < r2.length; j++) {
          const c2 = r2[j] as Hex;
          if (!free(c2) || r2.slice(0, j).some((x) => !free(x)) || same(c2, to)) break;
          for (const t2 of aimAt(c2, e, to)) {
            const e2 = (e + (MIRROR_TURN[t2] as number)) % 6;
            const cs = between(c2, e2, to);
            if (cs.slice(0, -1).every((x) => free(x) && !same(x, c))) out.push({ at: from, d, m: [{ c, t }, { c: c2, t: t2 }], cells: [...r0.slice(0, i + 1), ...r2.slice(0, j + 1), ...cs] });
          }
        }
      }
    }
    return out;
  };
  // The divider: on the first's other turn, straight or after a second mirror.
  const sCand: { s: Hex; dIn: number; pre: { c: Hex; t: number }[]; cells: Hex[] }[] = [];
  const r1 = ray(m1, dS);
  for (let i = 1; i < r1.length && i < 6; i++) {
    const c = r1[i] as Hex;
    if (!free(c) || r1.slice(0, i).some((x) => !free(x))) break;
    if (!oPath.some((x) => same(x, c))) sCand.push({ s: c, dIn: dS, pre: [], cells: r1.slice(0, i + 1) });
    // Or a mirror here, then the divider after it.
    for (let t = 0; t < 2; t++) {
      const e = (dS + (MIRROR_TURN[t] as number)) % 6;
      const r2 = ray(c, e);
      for (let j = 1; j < r2.length && j < 6; j++) {
        const s = r2[j] as Hex;
        if (!free(s) || r2.slice(0, j).some((x) => !free(x))) break;
        if (!oPath.some((x) => same(x, s))) sCand.push({ s, dIn: e, pre: [{ c, t }], cells: [...r1.slice(0, i + 1), ...r2.slice(0, j + 1)] });
      }
    }
  }
  // Shuffled by the seed, the first that reaches both.
  for (let tries = 0; tries < 24 && sCand.length; tries++) {
    const k = rngInt(rng, sCand.length);
    const S = sCand[k] as (typeof sCand)[number];
    sCand.splice(k, 1);
    const save = used.length;
    for (const p of S.pre) used.push(p.c);
    used.push(S.s);
    const so = rngInt(rng, 2);
    const outs = [S.dIn, (S.dIn + (SPLIT_TURN[so] as number)) % 6];
    const swap = rngInt(rng, 2) === 0;
    const toA = swap ? outs[1] : outs[0];
    const toD = swap ? outs[0] : outs[1];
    const la = legsTo(S.s, toA as number, A);
    const ld = legsTo(S.s, toD as number, D);
    let got: [Leg, Leg] | null = null;
    for (let n = 0; n < 12 && la.length && ld.length && !got; n++) {
      const a = pick(rng, la) as Leg;
      const b = pick(rng, ld) as Leg;
      const ca = a.m.map((x) => x.c);
      const cb = b.m.map((x) => x.c);
      if (ca.some((x) => cb.some((y) => same(x, y)))) continue;
      if (ca.some((x) => b.cells.some((y) => same(x, y))) || cb.some((x) => a.cells.some((y) => same(x, y)))) continue;
      got = [a, b];
    }
    if (!got) {
      used.length = save;
      continue;
    }
    const [a, b] = got;
    const m: Hex[] = [m1, o.c, ...S.pre.map((p) => p.c), ...a.m.map((p) => p.c), ...b.m.map((p) => p.c)];
    if (m.length > 6) {
      used.length = save;
      continue;
    }
    const turns = [tS, o.t, ...S.pre.map((p) => p.t), ...a.m.map((p) => p.t), ...b.m.map((p) => p.t)];
    path.push(...S.cells, ...a.cells, ...b.cells);
    return { m, sp: [S.s], turns, splits: [so], path, obvious: [{ m: m1, t: tO }, { m: o.c, t: o.t }], m1t: tS };
  }
  return null;
};

/** The nave by the seed: its puzzle with one way that lights both, its columns, its lamps. */
export const naveLayout = (seed: number): NaveLayout => {
  const cached = CACHE.get(seed);
  if (cached) return cached;
  const R = NAVE_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xe4a1));
  // The axis: the altar at the north end, the door at the south; the lantern under the hole, in the middle.
  // (The altar on the north row, the door on the south one, near the axis: x in half hexes, 2q + r.)
  const onRow = (r: number, w: number): Hex[] => {
    const out: Hex[] = [];
    for (let q = -9; q <= 9; q++) if (Math.abs(2 * q + r) <= w && inNave(q, r)) out.push({ q, r });
    return out;
  };
  const alts = onRow(-7, 3);
  const doors = onRow(7, 3);
  const mids = [...onRow(-1, 3), ...onRow(0, 2), ...onRow(1, 3)];
  let out: NaveLayout | null = null;
  for (let attempt = 0; attempt < 600 && !out; attempt++) {
    const A = pick(rng, alts) as Hex;
    const D = pick(rng, doors) as Hex;
    const l0 = pick(rng, mids) as Hex;
    const L = { q: l0.q, r: l0.r, dir: rngInt(rng, 2) === 0 ? 0 : 3 };
    const d = draft(rng, L, A, D);
    if (!d) continue;
    const onWay = (h: Hex): boolean => d.path.some((x) => same(x, h));
    const obCells = [...between(L, L.dir, d.m[0] as Hex)];
    const taken = (h: Hex): boolean => same(h, L) || same(h, A) || same(h, D) || d.m.some((x) => same(x, h)) || d.sp.some((x) => same(x, h));
    // The second divider: right by the way, never on it (nor on the obvious way).
    const near: Hex[] = [];
    for (let r = -7; r <= 7; r++)
      for (let q = -9; q <= 9; q++) {
        const h = { q, r };
        if (!inNave(q, r) || taken(h) || onWay(h) || obCells.some((x) => same(x, h)) || hexDist(h, L) < 2) continue;
        if (d.path.some((x) => hexDist(x, h) === 1) && Math.abs(r) < 7) near.push(h);
      }
    const s2 = pick(rng, near);
    if (!s2) continue;
    const sp = [d.sp[0] as Hex, s2];
    const m = [...d.m];
    const turns = [...d.turns];
    // The rest: mirrors that lead nowhere, off the way, not by the lantern, not crowding the altar or the door.
    for (let k = 0; k < 40 && m.length < 6; k++) {
      const h = { q: rngInt(rng, 13) - 6, r: rngInt(rng, 13) - 6 };
      if (!inNave(h.q, h.r) || taken(h) || same(h, s2) || onWay(h) || hexDist(h, L) < 2 || hexDist(h, A) < 2 || hexDist(h, D) < 2 || m.some((x) => hexDist(x, h) < 2) || sp.some((x) => hexDist(x, h) < 2)) continue;
      m.push(h);
      turns.push(rngInt(rng, 3));
    }
    if (m.length < 6) continue;
    // Numbered from the altar down.
    const idx = m.map((_, i) => i).sort((a, b) => sortHex(m[a] as Hex, m[b] as Hex));
    const mirrors = idx.map((i) => m[i] as Hex);
    const B: NaveBoard = { lantern: L, altar: A, door: D, mirrors, splits: sp };
    const sols = naveSolve(B);
    if (sols.length !== 1) continue;
    const sol = sols[0] as { turns: number[]; splits: number[]; touched: number };
    // Its way uses the first divider and not the second; and the obvious way lights the altar alone.
    if ((sol.touched >> 7) & 1 || !((sol.touched >> 6) & 1)) continue;
    const ot = idx.map((i) => turns[i] as number);
    for (const ob of d.obvious) ot[mirrors.findIndex((x) => same(x, ob.m))] = ob.t;
    const ob = naveTrace(B, ot, sol.splits);
    if (!ob.altar || ob.door || (ob.touched & 0xc0) !== 0 || popcount(ob.touched) !== 2) continue;
    // How they start: by the seed, lighting neither.
    let start0 = mirrors.map(() => rngInt(rng, 3));
    let split0 = [rngInt(rng, 2), rngInt(rng, 2)];
    // (Far from the answer: three of its pieces or more out of place, so turning by chance doesn't stumble on it.)
    const off = (t: number[], sp: number[]): number => {
      let n = 0;
      for (let i = 0; i < 6; i++) if ((sol.touched >> i) & 1 && t[i] !== sol.turns[i]) n++;
      for (let i = 0; i < 2; i++) if ((sol.touched >> (6 + i)) & 1 && sp[i] !== sol.splits[i]) n++;
      return n;
    };
    for (let k = 0; k < 60; k++) {
      const s0 = naveTrace(B, start0, split0);
      if (!s0.altar && !s0.door && off(start0, split0) >= 3) break;
      start0 = mirrors.map(() => rngInt(rng, 3));
      split0 = [rngInt(rng, 2), rngInt(rng, 2)];
    }
    const s0 = naveTrace(B, start0, split0);
    if (s0.altar || s0.door || off(start0, split0) < 3) continue;
    // Aud's mirror: the one on the way nearest the altar.
    let aud = -1;
    let ad = 99;
    mirrors.forEach((x, i) => {
      if (!((sol.touched >> i) & 1)) return;
      const dd = hexDist(x, A);
      if (dd < ad) {
        ad = dd;
        aud = i;
      }
    });
    if (aud < 0) continue;
    out = finish(R, B, sol, { turns: ot, touched: ob.touched }, start0, split0, aud);
  }
  if (!out) throw new Error(`naveLayout: no nave for seed ${seed}`);
  CACHE.set(seed, out);
  if (CACHE.size > 32) CACHE.delete(CACHE.keys().next().value as number);
  return out;
};

const CACHE = new Map<number, NaveLayout>();

const popcount = (x: number): number => {
  let n = 0;
  for (let v = x; v; v &= v - 1) n++;
  return n;
};

/** Toward the nave's axis from a column: the cell beside it a hero stands on to turn it (one the door's way reaches). */
const standBy = (h: Hex, blocked: (h: Hex) => boolean): { x: number; y: number } => {
  let best: Hex | null = null;
  let bd = 99;
  for (let d = 0; d < 6; d++) {
    const c = step(h, d);
    if (!inNave(c.q, c.r) || blocked(c)) continue;
    const ax = Math.abs(2 * c.q + c.r);
    if (ax < bd) {
      bd = ax;
      best = c;
    }
  }
  const c = hexToWorld((best ?? h).q, (best ?? h).r);
  const o = hexToWorld(h.q, h.r);
  // (A little toward the column: within reach of it.)
  return { x: o.x + idiv((c.x - o.x) * 8, 10), y: o.y + idiv((c.y - o.y) * 8, 10) };
};

const finish = (R: number, B: NaveBoard, sol: { turns: number[]; splits: number[]; touched: number }, obvious: { turns: number[]; touched: number }, start0: number[], split0: number[], aud: number): NaveLayout => {
  const cols = [...B.mirrors, ...B.splits];
  const rocks: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R && (!inNave(q, r) || cols.some((c) => same(c, { q, r })) || same({ q, r }, B.altar))) rocks.push({ q, r });
  // The door's cell and the atrium's way through the south wall stay open.
  const doorOut = [B.door, step(B.door, 5), step(B.door, 4)];
  const open = (h: Hex): boolean => doorOut.some((x) => same(x, h));
  const walls = rocks.filter((h) => !open(h)).sort(sortHex);
  // What the door's way reaches (a cell shut in by columns is as good as a wall: no one stands there).
  const reach = new Set<string>();
  const queue: Hex[] = [B.door];
  reach.add(`${B.door.q},${B.door.r}`);
  while (queue.length) {
    const c = queue.shift() as Hex;
    for (let d = 0; d < 6; d++) {
      const n = step(c, d);
      const k = `${n.q},${n.r}`;
      if (reach.has(k) || !inNave(n.q, n.r) || walls.some((x) => same(x, n))) continue;
      reach.add(k);
      queue.push(n);
    }
  }
  const blocked = (h: Hex): boolean => !reach.has(`${h.q},${h.r}`);
  const a = hexToWorld(B.altar.q, B.altar.r);
  const dw = hexToWorld(B.door.q, B.door.r);
  // Behind the altar, against the north wall: the loose stone Aud hid her mirror in.
  const st = hexToWorld(B.altar.q + 1, B.altar.r);
  // The ink on the walls: the floor's edge cells along both long walls, a few on each side.
  const ink: { x: number; y: number }[] = [];
  for (const r of [-4, -1, 2, 5]) {
    for (const side of [-1, 1]) {
      // The outermost floor cell on this row and side.
      let q = side < 0 ? idiv(-9 - r + 1, 2) : idiv(9 - r, 2);
      while (!inNave(q, r)) q -= side;
      const c = hexToWorld(q, r);
      ink.push({ x: c.x + side * 300, y: c.y });
    }
  }
  // The cells' doors: one in each long wall about the middle.
  const cells = [ink[2] as { x: number; y: number }, ink[5] as { x: number; y: number }].map((p) => ({ x: p.x, y: p.y }));
  // The memory's lamps: up the axis from the door to the altar, off it a little, side to side; never on a column, and
  // with room beside them toward the axis to stand and light them.
  const lamps: { x: number; y: number }[] = [];
  const taken = (h: Hex): boolean => blocked(h) || same(h, B.altar) || same(h, B.lantern) || lamps.some((l) => l.x === hexToWorld(h.q, h.r).x && l.y === hexToWorld(h.q, h.r).y);
  const rows = [5, 3, 1, -1, -3];
  rows.forEach((r, i) => {
    const side = i % 2 === 0 ? -1 : 1;
    // The cell on this row nearest a hex and a half off the axis, then the ones by it.
    const q0 = idiv(side * 3 - r + (side * 3 - r < 0 ? -1 : 0), 2);
    for (const dq of [0, side, -side, 2 * side, -2 * side]) {
      const h = { q: q0 + dq, r };
      const toward = step(h, side < 0 ? 0 : 3);
      if (!inNave(h.q, h.r) || taken(h) || blocked(toward) || same(toward, B.altar)) continue;
      lamps.push(hexToWorld(h.q, h.r));
      return;
    }
    lamps.push(hexToWorld(0, r));
  });
  lamps.push({ x: a.x + (a.x > 0 ? -1000 : 1000), y: a.y + 600 });
  const ms = B.mirrors.map((h) => standBy(h, (c) => blocked(c) || same(c, B.altar)));
  const ss = B.splits.map((h) => standBy(h, (c) => blocked(c) || same(c, B.altar)));
  return {
    ...B,
    radius: R,
    rocks: walls,
    answer: { turns: sol.turns, splits: sol.splits, touched: sol.touched },
    obvious,
    start0,
    split0,
    aud,
    stone: { x: st.x, y: st.y - 200 },
    mirrorStand: ms,
    splitStand: ss,
    rest: { x: a.x, y: a.y + 700 },
    piet: { x: a.x + (a.x > 0 ? -900 : 900), y: a.y + 900 },
    start: { x: dw.x, y: dw.y - 900 },
    ink,
    cells,
    lamps,
    atrium: { x: dw.x, y: dw.y + 1300 },
  };
};
